import { useState, useEffect, useRef, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  fetchMessages,
  addNewMessage,
  updateReplied,
  clearChats,
} from "../../store/admin/chatSlice";
import {
  fetchInternalNote,
  addInternalNote,
  clearNotes,
} from "../../store/admin/notesSlice";
import { useParams } from "react-router-dom";
import InternalNotes from "./InternalNotes";
import { isWorkflowEmployee } from "../../utils/employeeRoles";

import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import DOMPurify from "dompurify";
import { isChatMessageOnRight } from "../../utils/chatMessageSide";

const Chat = () => {
  const dispatch = useAppDispatch();

  const { user } = useAppSelector((state) => state.auth);
  const { employee } = useAppSelector((state) => state.allocationAuth);
  const { isImpersonating } = useAppSelector((state) => state.impersonation);
  const { chats } = useAppSelector((state) => state.adminChat);
  const { selectedCase } = useAppSelector((state) => state.adminCases);

  const [newNote, setNewNote] = useState("");
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);

  const { case_id } = useParams<{ case_id: string }>();

  const chatContainerRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastSentRef = useRef<number>(0);

  const isReadOnly =
    user?.role === "Admin Read" ||
    user?.role === "Production" ||
    user?.role === "Side Admin" ||
    isWorkflowEmployee(employee?.role);

  // Rich Text Toolbar
  const quillModules = {
    toolbar: [
      ["bold", "italic", "underline"],
      [{ color: [] }, { background: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ align: [] }],
      // ["clean"],
    ],
  };

  const handleAddNote = useCallback(() => {
    if (!newNote.trim()) return;

    dispatch(
      addInternalNote({
        case_id: case_id || "",
        author:
          user?.role === "Super Admin"
            ? user.user_name
            : employee?.emp_name || "Unknown",
        note: newNote.trim(),
      }),
    ).then(() => {
      setNewNote("");
    });
  }, [newNote, case_id, dispatch, user, employee]);

  const scrollToBottom = useCallback(() => {
    if (chatContainerRef.current) {
      requestAnimationFrame(() => {
        chatContainerRef.current!.scrollTop =
          chatContainerRef.current!.scrollHeight;
      });
    }
  }, []);

  const markAsReplied = useCallback(() => {
    if (isImpersonating) return;
    if (
      case_id &&
      user?.user_id &&
      !["production", "admin read", "side admin"].includes(
        user?.role?.toLowerCase() || "",
      )
    ) {
      dispatch(updateReplied({ case_id, replied: true, userId: user.user_id }));
    }
  }, [case_id, dispatch, user, isImpersonating]);

  const fetchMessagesCallback = useCallback(() => {
    if (!case_id) return;

    dispatch(fetchInternalNote(case_id));

    dispatch(
      fetchMessages({
        case_id,
        userId: user?.user_id?.toString() || "",
      }),
    ).finally(() => {
      markAsReplied();
    });
  }, [case_id, dispatch, user, markAsReplied]);

  const clearChatsCallback = useCallback(() => {
    dispatch(clearChats());
    dispatch(clearNotes());
  }, [dispatch]);

  // Initial Fetch
  useEffect(() => {
    if (case_id) {
      fetchMessagesCallback();
      dispatch(fetchInternalNote(case_id));
    }

    return () => {
      clearChatsCallback();
    };
  }, [case_id, fetchMessagesCallback, clearChatsCallback, dispatch]);

  // Polling
  useEffect(() => {
    if (!case_id) return;

    intervalRef.current = setInterval(fetchMessagesCallback, 10000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, [case_id, fetchMessagesCallback]);

  // Auto Scroll
  useEffect(() => {
    scrollToBottom();
  }, [chats, scrollToBottom]);

  const sendMessage = useCallback(() => {
    const cleanedMessage = newMessage
      .replace(/<(.|\n)*?>/g, "")
      .trim();

    if (
      !cleanedMessage ||
      !user?.user_id ||
      isReadOnly ||
      sending ||
      Date.now() - lastSentRef.current < 500
    ) {
      return;
    }

    lastSentRef.current = Date.now();
    setSending(true);

    dispatch(
      addNewMessage({
        case_id: case_id || "",
        user_id: user.user_id,
        content: newMessage, // HTML STRING
        type: "",
        by:
          user?.role === "Super Admin"
            ? user?.user_name
            : employee
              ? employee.emp_name
              : user.role === "User" || user.role === "Side Admin"
                ? "Client"
                : "system",
      }),
    )
      .unwrap()
      .then(() => {
        setNewMessage("");
        fetchMessagesCallback();
        markAsReplied();
      })
      .catch(console.error)
      .finally(() => setSending(false));
  }, [
    newMessage,
    user,
    isReadOnly,
    sending,
    case_id,
    dispatch,
    employee,
    fetchMessagesCallback,
    markAsReplied,
  ]);

  const renderMessage = (msg: any, index: number) => {
    const msgType = msg.type?.toLowerCase();

    const isSystem = msgType === "system";
    const isSystemUpdate = msgType === "system_update";
    const isRedesign = msgType === "redesign_update";

    // Right/blue = my side for this viewer in Admin Cases.
    const caseOwnerUserId =
      selectedCase?.case_id === case_id ? selectedCase?.user_id : undefined;
    const isMine = isChatMessageOnRight(msg, {
      myUserId: user?.user_id,
      myRole: user?.role,
      viewContext: "admin-cases",
      caseOwnerUserId,
    });

    // System Messages
    if (isSystem || isSystemUpdate || isRedesign) {
      return (
        <div
          key={index}
          className="flex justify-center w-full text-gray-600 text-xs"
        >
          <div
            className={`flex flex-col items-center px-4 py-1 rounded-lg shadow-sm ${isSystem
              ? "bg-gray-100"
              : isSystemUpdate
                ? "bg-gray-100"
                : "bg-red-500 text-white"
              }`}
          >
            <span>{new Date(msg.timestamp).toLocaleString("en-GB")}</span>

            <span>{msg.content}</span>
          </div>
        </div>
      );
    }

    return (
      <div
        key={index}
        className={`flex ${isMine ? "justify-end" : "justify-start"}`}
      >
        <div
          className={`max-w-[70%] px-3 py-2 rounded-lg shadow-sm ${isMine
            ? "bg-blue-500 text-white"
            : "bg-gray-200 text-gray-800"
            }`}
        >
          <div className="text-sm break-words overflow-hidden">
            {/* RICH TEXT RENDER */}
            <div
              className="message-content w-full overflow-hidden"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(msg.content),
              }}
            />

            <span className="text-[10px] block mt-2 opacity-70">
              {new Date(msg.timestamp).toLocaleString("en-GB")}
            </span>

            {user && user.role === "Super Admin" ? (
              <span className="text-[10px] block opacity-70">
                By : {msg.comment_By}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  if (!user?.user_id) {
    return (
      <div className="text-center text-red-500">
        User not found
      </div>
    );
  }

  return (
    <div className="bg-white text-sm w-full h-[60vh] flex flex-col">
      <div className="flex flex-1 gap-4 overflow-hidden flex-col md:flex-row">
        {/* LEFT PANEL */}
        <div className="md:w-[40%] w-full flex flex-col border rounded-lg p-3 bg-gray-50 shadow-sm">
          <div className="bg-white border rounded-xl shadow-sm p-2 text-sm flex flex-col max-h-[75vh] overflow-hidden">
            <h2 className="font-semibold text-sm text-gray-800 mb-1">
              Internal Notes
            </h2>

            <InternalNotes />
          </div>

          {/* ADD NOTE */}
          <div className="bg-white border rounded-xl shadow-sm p-2 space-y-1 mt-3">
            {["Admin Read", "Production", "Side Admin"].includes(
              user?.role || "",
            ) ? (
              <p className="text-gray-500 text-xs">
                Read-Only Mode
              </p>
            ) : (
              <>
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Write a note..."
                  className="w-full p-2 border rounded-md text-xs resize-none focus:ring-1 focus:ring-blue-300"
                  rows={2}
                />

                <button
                  onClick={handleAddNote}
                  disabled={!newNote.trim()}
                  className={`px-2 py-1 text-xs rounded-md font-medium transition ${newNote.trim()
                    ? "bg-blue-600 text-white hover:bg-blue-700"
                    : "bg-gray-300 text-gray-600 cursor-not-allowed"
                    }`}
                >
                  Add Note
                </button>
              </>
            )}
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="md:w-[60%] w-full flex flex-col border rounded-lg bg-white overflow-hidden">
          {/* CHAT */}
          <div
            ref={chatContainerRef}
            className="flex-1 overflow-y-auto p-4 space-y-3"
          >
            {chats.length === 0 ? (
              <p className="text-center text-gray-400">
                No messages yet.
              </p>
            ) : (
              chats.map(renderMessage)
            )}
          </div>

          {/* MESSAGE INPUT */}
          <div className="border-t p-3 flex flex-col gap-3 bg-white">
            <div className="">
              <ReactQuill
                theme="snow"
                value={newMessage}
                onChange={setNewMessage}
                modules={quillModules}
                readOnly={isReadOnly || sending}
                placeholder={
                  isReadOnly
                    ? "Read-only mode"
                    : "Type a message..."
                }
                className="custom-quill"
              />
            </div>

            <button
              onClick={sendMessage}
              disabled={
                isReadOnly ||
                sending ||
                !newMessage.replace(/<(.|\n)*?>/g, "").trim()
              }
              className="bg-blue-500 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {sending ? "Sending..." : "Send"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chat;









// import { useState, useEffect, useRef, useCallback } from "react";
// import { useAppDispatch, useAppSelector } from "../../store/hooks";
// import {
//   fetchMessages,
//   addNewMessage,
//   updateReplied,
//   clearChats,
// } from "../../store/admin/chatSlice";
// import {
//   fetchInternalNote,
//   addInternalNote,
//   clearNotes
// } from "../../store/admin/notesSlice";
// import { useParams } from "react-router-dom";
// import InternalNotes from "./InternalNotes";

// const Chat = () => {
//   const dispatch = useAppDispatch();

//   const { user } = useAppSelector((state) => state.auth);
//   const { employee } = useAppSelector((state) => state.allocationAuth);
//   const { chats } = useAppSelector((state) => state.adminChat);

//   const [newNote, setNewNote] = useState("");
//   const [newMessage, setNewMessage] = useState("");
//   const [sending, setSending] = useState(false);

//   const { case_id } = useParams<{ case_id: string }>();
//   const chatContainerRef = useRef<HTMLDivElement>(null);
//   const inputRef = useRef<HTMLInputElement>(null);
//   const intervalRef = useRef<NodeJS.Timeout | null>(null);
//   const lastSentRef = useRef<number>(0);

//   const isReadOnly =
//     user?.role === "Admin Read" ||
//     user?.role === "Designer" ||
//     user?.role === "Side Admin";

//   const handleAddNote = useCallback(() => {
//     if (!newNote.trim()) return;
//     dispatch(
//       addInternalNote({
//         case_id: case_id || "",
//         author: user?.role === "Super Admin" ? user.user_name : employee?.emp_name || "Unknown",
//         note: newNote.trim(),
//       }),
//     ).then(() => {
//       setNewNote("");
//     });
//   }, [newNote, case_id, dispatch]);

//   const scrollToBottom = useCallback(() => {
//     if (chatContainerRef.current) {
//       requestAnimationFrame(() => {
//         chatContainerRef.current!.scrollTop =
//           chatContainerRef.current!.scrollHeight;
//       });
//     }
//   }, []);

//   const markAsReplied = useCallback(() => {
//     if (
//       case_id &&
//       user?.user_id &&
//       !["designer", "admin read", "side admin"].includes(
//         user?.role?.toLowerCase() || "",
//       )
//     ) {
//       dispatch(updateReplied({ case_id, replied: true, userId: user.user_id }));
//     }
//   }, [case_id, dispatch, user?.user_id, user?.role]);

//   const fetchMessagesCallback = useCallback(() => {
//     if (!case_id) return;
//     dispatch(fetchInternalNote(case_id));
//     dispatch(
//       fetchMessages({ case_id, userId: user?.user_id?.toString() || "" }),
//     ).finally(() => {
//       markAsReplied();
//     });
//   }, [case_id, dispatch, user?.user_id, markAsReplied]);

//   const clearChatsCallback = useCallback(
//     () => {dispatch(clearChats())
//     dispatch(clearNotes())},
//     [dispatch],
//   );

//   // Initial fetch
//   useEffect(() => {
//     if (case_id) {fetchMessagesCallback();
//       dispatch(fetchInternalNote(case_id))
//     }
//     return () => {
//       clearChatsCallback();
//     };
//   }, [case_id, fetchMessagesCallback, clearChatsCallback]);

//   // Polling every 10s
//   useEffect(() => {
//     if (!case_id) return;
//     intervalRef.current = setInterval(fetchMessagesCallback, 10000);
//     return () => {
//       if (intervalRef.current) clearInterval(intervalRef.current);
//       intervalRef.current = null;
//     };
//   }, [case_id, fetchMessagesCallback]);

//   // Auto-scroll on messages change
//   useEffect(() => {
//     scrollToBottom();
//   }, [chats, scrollToBottom]);

//   // Auto-focus input
//   useEffect(() => inputRef.current?.focus(), []);

//   const sendMessage = useCallback(() => {
//     const trimmedMessage = newMessage.trim();
//     if (
//       !trimmedMessage ||
//       !user?.user_id ||
//       isReadOnly ||
//       sending ||
//       Date.now() - lastSentRef.current < 500
//     )
//       return;

//     lastSentRef.current = Date.now();
//     setSending(true);

//     dispatch(
//       addNewMessage({
//         case_id: case_id || "",
//         user_id: user.user_id,
//         content: trimmedMessage,
//         type: "",
//         by : user?.role === "Super Admin" ? user?.user_name : employee ? employee.emp_name : user.role === "User" || user.role === "Side Admin" ? "Client" : "system",
//       }),
//     )
//       .unwrap()
//       .then(() => {
//         setNewMessage("");
//         fetchMessagesCallback();
//         markAsReplied();
//       })
//       .catch(console.error)
//       .finally(() => setSending(false));
//   }, [
//     newMessage,
//     user?.user_id,
//     isReadOnly,
//     sending,
//     case_id,
//     dispatch,
//     fetchMessagesCallback,
//     markAsReplied,
//   ]);

//   const renderMessage = (msg: any, index: number) => {
//     const msgType = msg.type?.toLowerCase();
//     const isSystem = msgType === "system";
//     const isSystemUpdate = msgType === "system_update";
//     const isRedesign = msgType === "redesign_update";
//     const isAdmin = [
//       "admin_comment",
//       "super admin_comment",
//       "crm_comment",
//     ].includes(msgType);

//     if (isSystem || isSystemUpdate || isRedesign) {
//       return (
//         <div
//           key={index}
//           className="flex justify-center w-full text-gray-600 text-xs"
//         >
//           <div
//             className={`flex flex-col items-center px-4 py-1 rounded-lg shadow-sm ${
//               isSystem
//                 ? "bg-gray-100"
//                 : isSystemUpdate
//                   ? "bg-gray-100"
//                   : "bg-red-500 text-white"
//             }`}
//           >
//             <span>{new Date(msg.timestamp).toLocaleString("en-GB")}</span>
//             <span>{msg.content}</span>
//           </div>
//         </div>
//       );
//     }

//     return (
//       <div
//         key={index}
//         className={`flex ${isAdmin ? "justify-end" : "justify-start"}`}
//       >
//         <div
//           className={`max-w-[70%] px-2 py-1 rounded-lg ${
//             isAdmin ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-800"
//           }`}
//         >
//           <div className="text-sm flex flex-col">
//             {msg.content}
//             <span className="text-[10px]">
//               {new Date(msg.timestamp).toLocaleString("en-GB")}
//             </span>
//             {user && user.role === "Super Admin" ? (
//               <span className="text-[10px]">By : {msg.comment_By}</span>
//             ) : (null)
//           }
//           </div>
//         </div>
//       </div>
//     );
//   };

//   if (!user?.user_id) {
//     return <div className="text-center text-red-500">User not found</div>;
//   }

//   return (
//     <div className="bg-white text-sm w-full h-[60vh] flex flex-col">
//       <div className="flex flex-1 gap-4 overflow-hidden">
//         {/* Left Panel - Internal Notes */}
//         <div className="md:w-[40%] w-full flex flex-col border rounded-lg p-3 bg-gray-50 shadow-sm">
//           <div className="bg-white border rounded-xl shadow-sm p-2 text-sm flex flex-col max-h-[75vh] overflow-hidden">
//             <h2 className="font-semibold text-sm text-gray-800 mb-1">
//               Internal Notes
//             </h2>
//             <InternalNotes />
//           </div>

//           {/* Add Note */}
//           <div className="bg-white border rounded-xl shadow-sm p-2 space-y-1">
//             {["Admin Read", "Designer", "Side Admin"].includes(
//               user?.role || "",
//             ) ? (
//               <p className="text-gray-500 text-xs">Read-Only Mode</p>
//             ) : (
//               <>
//                 <textarea
//                   value={newNote}
//                   onChange={(e) => setNewNote(e.target.value)}
//                   placeholder="Write a note..."
//                   className="w-full p-1 border rounded-md text-xs resize-none focus:ring-1 focus:ring-blue-300"
//                   rows={2}
//                 />
//                 <button
//                   onClick={handleAddNote}
//                   disabled={!newNote.trim()}
//                   className={`px-2 py-1 text-xs rounded-md font-medium transition ${
//                     newNote.trim()
//                       ? "bg-blue-600 text-white hover:bg-blue-700"
//                       : "bg-gray-300 text-gray-600 cursor-not-allowed"
//                   }`}
//                 >
//                   Add Note
//                 </button>
//               </>
//             )}
//           </div>
//         </div>

//         {/* Right Panel - Chat Messages */}
//         <div className="md:w-[60%] w-full flex flex-col border rounded-lg bg-white">
//           <div
//             ref={chatContainerRef}
//             className="flex-1 overflow-y-auto p-4 space-y-3"
//           >
//             {chats.length === 0 ? (
//               <p className="text-center text-gray-400">No messages yet.</p>
//             ) : (
//               chats.map(renderMessage)
//             )}
//           </div>
//           <div className="flex flex-col gap-2 mt-auto">
//             <input
//               type="text"
//               ref={inputRef}
//               value={newMessage}
//               onChange={(e) => setNewMessage(e.target.value)}
//               onKeyDown={(e) =>
//                 e.key === "Enter" && !isReadOnly && sendMessage()
//               }
//               placeholder={isReadOnly ? "Read-only mode" : "Type a message..."}
//               className="border rounded-md p-2 text-sm outline-none focus:ring-2 focus:ring-blue-400 disabled:bg-gray-200 disabled:cursor-not-allowed"
//               disabled={isReadOnly || sending}
//             />
//             <button
//               onClick={sendMessage}
//               disabled={isReadOnly || sending || !newMessage.trim()}
//               className="bg-blue-500 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
//             >
//               {sending ? "Sending..." : "Send"}
//             </button>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default Chat;